import { 
  Firestore, 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  PetConversationSummary, 
  RecentMemoryContext, 
  MemorySearchFilters, 
  MemoryAnalytics,
  ConversationMemoryRequest,
  MemoryGenerationResult,
  MemoryRetrievalResult,
  MEMORY_TEXTS
} from '../types/recentMemory';

/**
 * Recent Conversation Memory Service
 * 
 * Manages conversation summaries for pets over the last 3 months,
 * providing context-aware AI responses and personalized recommendations.
 */
export class RecentConversationMemoryService {
  private db: Firestore;
  private memoryCache: Map<string, PetConversationSummary[]> = new Map();
  private contextCache: Map<string, RecentMemoryContext> = new Map();

  constructor() {
    this.db = db;
  }

  /**
   * Generate and store a conversation summary for a pet
   */
  async generateConversationSummary(request: ConversationMemoryRequest): Promise<MemoryGenerationResult> {
    const startTime = Date.now();
    
    try {
      console.log('🧠 Generating conversation summary for pet:', request.petName);
      
      // Extract key information from conversation
      const summary = await this.extractConversationSummary(request);
      
      // Store in Firestore (summary already contains timestamps)
      const summaryRef = await addDoc(collection(this.db, 'petConversationSummaries'), summary);

      // Update cache
      this.updateMemoryCache(request.userId, summary);

      // Update analytics
      await this.updateMemoryAnalytics(request.userId, request.petName);

      const processingTime = Date.now() - startTime;
      console.log('✅ Conversation summary generated successfully:', {
        petName: request.petName,
        processingTime: `${processingTime}ms`
      });

      return {
        success: true,
        summary: { ...summary, id: summaryRef.id },
        processingTime
      };

    } catch (error) {
      console.error('❌ Failed to generate conversation summary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Extract conversation summary using AI analysis
   */
  private async extractConversationSummary(request: ConversationMemoryRequest): Promise<Omit<PetConversationSummary, 'id'>> {
    const { petName, petType, messages, language, context } = request;
    
    // Get language-specific texts
    const texts = MEMORY_TEXTS[language] || MEMORY_TEXTS['en'];
    
    // Prepare conversation text for analysis
    const conversationText = messages
      .map(msg => `${msg.role}: ${msg.content}`)
      .join('\n');

    // Generate summary using OpenAI (simplified for now - in production, use actual OpenAI API)
    const summary = await this.generateAISummary(conversationText, texts, language);
    
    // Extract key topics
    const keyTopics = await this.extractKeyTopics(conversationText, texts, language);
    
    // Extract pet care advice
    const petCareAdvice = await this.extractPetCareAdvice(conversationText, texts, language);
    
    // Extract training progress
    const trainingProgress = await this.extractTrainingProgress(conversationText, texts, language);
    
    // Extract health notes
    const healthNotes = await this.extractHealthNotes(conversationText, texts, language);
    
    // Extract behavior observations
    const behaviorObservations = await this.extractBehaviorObservations(conversationText, texts, language);

    // Build summary object, filtering out undefined values for Firestore compatibility
    const summaryData: Omit<PetConversationSummary, 'id'> = {
      userId: request.userId, // Use the provided userId directly
      petName,
      petType,
      conversationId: request.conversationId,
      summary: summary,
      keyTopics,
      petCareAdvice,
      trainingProgress,
      healthNotes,
      behaviorObservations,
      createdAt: Timestamp.now(),
      lastUpdated: Timestamp.now(),
      language,
      conversationCount: 1, // Will be updated by analytics
      totalMessages: messages.length
    };

    // Only add optional fields if they have values
    if (context?.petBreed) {
      summaryData.petBreed = context.petBreed;
    }
    if (context?.petAge) {
      summaryData.petAge = context.petAge;
    }

    return summaryData;
  }

  /**
   * Generate AI summary of conversation
   */
  private async generateAISummary(conversationText: string, texts: any, language: string): Promise<string> {
    // In production, this would use OpenAI API
    // For now, return a basic summary
    const lines = conversationText.split('\n');
    const userMessages = lines.filter(line => line.startsWith('user:'));
    const assistantMessages = lines.filter(line => line.startsWith('assistant:'));
    
    return `${texts.summaryPrompt} This conversation involved ${userMessages.length} user messages and ${assistantMessages.length} assistant responses about pet care.`;
  }

  /**
   * Extract key topics from conversation
   */
  private async extractKeyTopics(conversationText: string, texts: any, language: string): Promise<string[]> {
    // In production, this would use OpenAI API for topic extraction
    // For now, return basic topics based on keywords
    const topics: string[] = [];
    
    if (conversationText.toLowerCase().includes('training')) topics.push('Training');
    if (conversationText.toLowerCase().includes('health')) topics.push('Health');
    if (conversationText.toLowerCase().includes('behavior')) topics.push('Behavior');
    if (conversationText.toLowerCase().includes('feeding')) topics.push('Feeding');
    if (conversationText.toLowerCase().includes('exercise')) topics.push('Exercise');
    if (conversationText.toLowerCase().includes('grooming')) topics.push('Grooming');
    
    return topics.length > 0 ? topics : ['General Pet Care'];
  }

  /**
   * Extract pet care advice from conversation
   */
  private async extractPetCareAdvice(conversationText: string, texts: any, language: string): Promise<string[]> {
    // In production, this would use OpenAI API
    // For now, return basic advice extraction
    const advice: string[] = [];
    
    if (conversationText.toLowerCase().includes('recommend')) {
      advice.push('Follow recommended care guidelines');
    }
    if (conversationText.toLowerCase().includes('schedule')) {
      advice.push('Maintain consistent care schedule');
    }
    if (conversationText.toLowerCase().includes('monitor')) {
      advice.push('Monitor pet behavior and health');
    }
    
    return advice.length > 0 ? advice : ['Continue current care routine'];
  }

  /**
   * Extract training progress from conversation
   */
  private async extractTrainingProgress(conversationText: string, texts: any, language: string): Promise<string[]> {
    const progress: string[] = [];
    
    if (conversationText.toLowerCase().includes('improved')) {
      progress.push('Showing improvement in training');
    }
    if (conversationText.toLowerCase().includes('challenge')) {
      progress.push('Working through training challenges');
    }
    if (conversationText.toLowerCase().includes('success')) {
      progress.push('Achieving training milestones');
    }
    
    return progress.length > 0 ? progress : ['Training in progress'];
  }

  /**
   * Extract health notes from conversation
   */
  private async extractHealthNotes(conversationText: string, texts: any, language: string): Promise<string[]> {
    const notes: string[] = [];
    
    if (conversationText.toLowerCase().includes('vet')) {
      notes.push('Veterinary consultation mentioned');
    }
    if (conversationText.toLowerCase().includes('symptom')) {
      notes.push('Health symptoms discussed');
    }
    if (conversationText.toLowerCase().includes('medication')) {
      notes.push('Medication or treatment discussed');
    }
    
    return notes.length > 0 ? notes : ['No specific health concerns noted'];
  }

  /**
   * Extract behavior observations from conversation
   */
  private async extractBehaviorObservations(conversationText: string, texts: any, language: string): Promise<string[]> {
    const observations: string[] = [];
    
    if (conversationText.toLowerCase().includes('calm')) {
      observations.push('Calm behavior observed');
    }
    if (conversationText.toLowerCase().includes('active')) {
      observations.push('Active behavior noted');
    }
    if (conversationText.toLowerCase().includes('social')) {
      observations.push('Social behavior discussed');
    }
    
    return observations.length > 0 ? observations : ['General behavior patterns observed'];
  }

  /**
   * Get recent conversation summaries for a pet
   */
  async getRecentConversationSummaries(
    userId: string, 
    petName: string, 
    filters?: MemorySearchFilters
  ): Promise<MemoryRetrievalResult> {
    const startTime = Date.now();
    
    try {
      console.log('🔍 Retrieving recent conversation summaries for pet:', petName);
      
      // Check cache first
      const cacheKey = `${userId}_${petName}`;
      if (this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        return {
          success: true,
          summaries: cached,
          retrievalTime: Date.now() - startTime
        };
      }

      // Build query
      let q = query(
        collection(this.db, 'petConversationSummaries'),
        where('userId', '==', userId),
        where('petName', '==', petName),
        orderBy('lastUpdated', 'desc'),
        limit(50) // Limit to last 50 conversations
      );

      // Apply additional filters
      if (filters?.dateRange) {
        q = query(
          q,
          where('lastUpdated', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('lastUpdated', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      if (filters?.petType) {
        q = query(q, where('petType', '==', filters.petType));
      }

      if (filters?.language) {
        q = query(q, where('language', '==', filters.language));
      }

      const snapshot = await getDocs(q);
      const summaries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PetConversationSummary[];

      // Update cache
      this.memoryCache.set(cacheKey, summaries);

      const retrievalTime = Date.now() - startTime;
      console.log('✅ Retrieved conversation summaries:', {
        petName,
        count: summaries.length,
        retrievalTime: `${retrievalTime}ms`
      });

      return {
        success: true,
        summaries,
        retrievalTime
      };

    } catch (error) {
      console.error('❌ Failed to retrieve conversation summaries:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get comprehensive memory context for a pet
   */
  async getPetMemoryContext(userId: string, petName: string): Promise<MemoryRetrievalResult> {
    try {
      console.log('🧠 Building memory context for pet:', petName);
      
      // Get recent summaries
      const summariesResult = await this.getRecentConversationSummaries(userId, petName);
      if (!summariesResult.success || !summariesResult.summaries) {
        return summariesResult;
      }

      const summaries = summariesResult.summaries;
      
      // Build overall pet profile
      const overallPetProfile = this.buildOverallPetProfile(summaries);
      
      // Create memory context
      const context: RecentMemoryContext = {
        petName,
        petType: summaries[0]?.petType || 'both',
        recentSummaries: summaries.slice(0, 10), // Last 10 conversations
        overallPetProfile,
        lastConversationDate: summaries[0]?.lastUpdated || Timestamp.now(),
        totalConversations: summaries.length
      };

      // Update context cache
      this.contextCache.set(`${userId}_${petName}`, context);

      return {
        success: true,
        context,
        retrievalTime: summariesResult.retrievalTime
      };

    } catch (error) {
      console.error('❌ Failed to build memory context:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Build overall pet profile from multiple conversation summaries
   */
  private buildOverallPetProfile(summaries: PetConversationSummary[]) {
    const personality: string[] = [];
    const preferences: string[] = [];
    const challenges: string[] = [];
    const achievements: string[] = [];
    const healthHistory: string[] = [];

    summaries.forEach(summary => {
      // Aggregate personality traits
      if (summary.behaviorObservations) {
        personality.push(...summary.behaviorObservations);
      }
      
      // Aggregate preferences
      if (summary.keyTopics) {
        preferences.push(...summary.keyTopics);
      }
      
      // Aggregate challenges
      if (summary.petCareAdvice) {
        challenges.push(...summary.petCareAdvice);
      }
      
      // Aggregate achievements
      if (summary.trainingProgress) {
        achievements.push(...summary.trainingProgress);
      }
      
      // Aggregate health history
      if (summary.healthNotes) {
        healthHistory.push(...summary.healthNotes);
      }
    });

    return {
      personality: Array.from(new Set(personality)), // Remove duplicates
      preferences: Array.from(new Set(preferences)),
      challenges: Array.from(new Set(challenges)),
      achievements: Array.from(new Set(achievements)),
      healthHistory: Array.from(new Set(healthHistory))
    };
  }

  /**
   * Update memory analytics
   */
  private async updateMemoryAnalytics(userId: string, petName: string): Promise<void> {
    try {
      // This would update analytics in a separate collection
      // For now, just log the update
      console.log('📊 Updating memory analytics for:', { userId, petName });
    } catch (error) {
      console.error('❌ Failed to update memory analytics:', error);
    }
  }

  /**
   * Update memory cache
   */
  private updateMemoryCache(userId: string, summary: Omit<PetConversationSummary, 'id'>): void {
    const cacheKey = `${userId}_${summary.petName}`;
    const existing = this.memoryCache.get(cacheKey) || [];
    
    // Add new summary to the beginning of the array
    const updated = [{ ...summary, id: 'temp' }, ...existing];
    
    // Keep only last 50 summaries in cache
    this.memoryCache.set(cacheKey, updated.slice(0, 50));
  }

  /**
   * Clear memory cache for a specific pet
   */
  clearPetMemoryCache(userId: string, petName: string): void {
    const cacheKey = `${userId}_${petName}`;
    this.memoryCache.delete(cacheKey);
    this.contextCache.delete(cacheKey);
  }

  /**
   * Clear all memory caches
   */
  clearAllMemoryCaches(): void {
    this.memoryCache.clear();
    this.contextCache.clear();
  }

  /**
   * Get memory analytics for a pet
   */
  async getMemoryAnalytics(userId: string, petName: string): Promise<MemoryAnalytics | null> {
    try {
      const summariesResult = await this.getRecentConversationSummaries(userId, petName);
      if (!summariesResult.success || !summariesResult.summaries) {
        return null;
      }

      const summaries = summariesResult.summaries;
      
      // Calculate analytics
      const mostDiscussedTopics = this.calculateMostDiscussedTopics(summaries);
      const petProgressTrends = this.calculatePetProgressTrends(summaries);
      const conversationPatterns = this.calculateConversationPatterns(summaries);
      const healthTracking = this.calculateHealthTracking(summaries);

      return {
        mostDiscussedTopics,
        petProgressTrends,
        conversationPatterns,
        healthTracking
      };

    } catch (error) {
      console.error('❌ Failed to get memory analytics:', error);
      return null;
    }
  }

  /**
   * Calculate most discussed topics
   */
  private calculateMostDiscussedTopics(summaries: PetConversationSummary[]) {
    const topicCounts = new Map<string, { count: number; lastDiscussed: Timestamp }>();
    
    summaries.forEach(summary => {
      summary.keyTopics?.forEach(topic => {
        const existing = topicCounts.get(topic);
        if (existing) {
          existing.count++;
          if (summary.lastUpdated > existing.lastDiscussed) {
            existing.lastDiscussed = summary.lastUpdated;
          }
        } else {
          topicCounts.set(topic, { count: 1, lastDiscussed: summary.lastUpdated });
        }
      });
    });

    return Array.from(topicCounts.entries())
      .map(([topic, data]) => ({ topic, frequency: data.count, lastDiscussed: data.lastDiscussed }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 10);
  }

  /**
   * Calculate pet progress trends
   */
  private calculatePetProgressTrends(summaries: PetConversationSummary[]) {
    return summaries
      .filter(s => s.trainingProgress && s.trainingProgress.length > 0)
      .map(summary => ({
        date: summary.lastUpdated,
        progress: summary.trainingProgress![0],
        improvement: summary.trainingProgress![0].toLowerCase().includes('improved') || 
                    summary.trainingProgress![0].toLowerCase().includes('success')
      }))
      .sort((a, b) => a.date.seconds - b.date.seconds);
  }

  /**
   * Calculate conversation patterns
   */
  private calculateConversationPatterns(summaries: PetConversationSummary[]) {
    const totalMessages = summaries.reduce((sum, s) => sum + s.totalMessages, 0);
    const averageLength = summaries.length > 0 ? totalMessages / summaries.length : 0;
    
    const allTopics = summaries.flatMap(s => s.keyTopics || []);
    const topicCounts = new Map<string, number>();
    allTopics.forEach(topic => {
      topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1);
    });
    
    const preferredTopics = Array.from(topicCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([topic]) => topic);

    return {
      averageConversationLength: Math.round(averageLength),
      mostActiveTimeOfDay: 'Evening', // This would be calculated from actual timestamps
      preferredTopics
    };
  }

  /**
   * Calculate health tracking data
   */
  private calculateHealthTracking(summaries: PetConversationSummary[]) {
    return summaries
      .filter(s => s.healthNotes && s.healthNotes.length > 0)
      .map(summary => ({
        date: summary.lastUpdated,
        healthNote: summary.healthNotes![0],
        severity: this.determineHealthSeverity(summary.healthNotes![0])
      }))
      .sort((a, b) => a.date.seconds - b.date.seconds);
  }

  /**
   * Determine health severity from note
   */
  private determineHealthSeverity(note: string): 'low' | 'medium' | 'high' {
    const lowerNote = note.toLowerCase();
    if (lowerNote.includes('emergency') || lowerNote.includes('urgent') || lowerNote.includes('serious')) {
      return 'high';
    }
    if (lowerNote.includes('concern') || lowerNote.includes('issue') || lowerNote.includes('problem')) {
      return 'medium';
    }
    return 'low';
  }
}

// Export singleton instance
export const recentConversationMemoryService = new RecentConversationMemoryService();
